// NOTICE: KAPTION-INTEGRATION
// security-center.UI.1 — one shared copy of the account's Security Center
// overview for the hub's tabs.
//
// Gate (both required): the KAPTION_SECURITY_CENTER flag through
// useKaptionFeatureFlags AND the `security_center` feature permission through
// useFeaturePermissions, and an administrator. The server applies the same gate
// (404 otherwise); then the overview stays empty and nothing renders.
import { ref, computed } from 'vue';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';

export const SECURITY_FLAG = 'KAPTION_SECURITY_CENTER';
export const SECURITY_FEATURE = 'security_center';

const overview = ref(null);
const loading = ref(false);
const failed = ref(false);
let inflight = null;

export const resetKaptionSecurityState = () => {
  overview.value = null;
  loading.value = false;
  failed.value = false;
  inflight = null;
};

export function useKaptionSecurity() {
  const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
  const { canUseFeature } = useFeaturePermissions();

  const isEnabled = computed(
    () => isKaptionFlagEnabled(SECURITY_FLAG) && canUseFeature(SECURITY_FEATURE)
  );

  const setOverview = data => {
    overview.value = data || null;
  };

  const fetchOverview = async ({ force = false } = {}) => {
    if (!isEnabled.value) return null;
    if (overview.value && !force) return overview.value;
    if (inflight) return inflight;

    loading.value = true;
    inflight = KaptionSecurityAPI.overview()
      .then(({ data }) => {
        failed.value = false;
        setOverview(data);
        return data;
      })
      .catch(() => {
        failed.value = true;
        setOverview(null);
        return null;
      })
      .finally(() => {
        loading.value = false;
        inflight = null;
      });
    return inflight;
  };

  const settings = computed(() => overview.value?.settings || {});
  const connection = computed(() => overview.value?.connection || null);
  const domains = computed(() => overview.value?.domains || []);
  const verifiedDomains = computed(() =>
    domains.value.filter(domain => domain.status === 'verified')
  );
  const providerLabel = computed(() => connection.value?.label || '');
  const isConnected = computed(() => connection.value?.status === 'enabled');
  const twoStepAvailable = computed(() => !!overview.value?.two_step_available);

  return {
    overview,
    loading,
    failed,
    isEnabled,
    settings,
    connection,
    domains,
    verifiedDomains,
    providerLabel,
    isConnected,
    twoStepAvailable,
    fetchOverview,
    setOverview,
  };
}
